import { useState, useEffect } from 'react';
import { shortDateFrom } from '../utils/date';
import Popup from './Popup';
import { FaRegCheckCircle, FaCheckCircle, FaRegTrashAlt } from 'react-icons/fa';
import { RiPaletteLine } from 'react-icons/ri';
import { MdClose } from 'react-icons/md';
import { Bar } from './Bar';
import { colors, colorsList, Color } from '../utils/colors';

interface Props {
  x: number;
  y: number;
  id: string;
  bar: Bar;
  isOpen: boolean;
  onEdit: (id: string, bar: Bar, keepEditing?: boolean) => void;
}

export default ({ x, y, id, bar, isOpen, onEdit }: Props) => {
  const [title, setTitle] = useState(bar?.title || '');
  const [done, setDone] = useState<boolean>(bar.done);
  const [description, setDescription] = useState<string | undefined>(bar.description || '');
  const [color, setColor] = useState<Color>(bar.color);

  function complete(keepEditing = false) {
    onEdit(
      id,
      {
        ...bar,
        title,
        color,
        description,
        done,
      },
      keepEditing
    );
  }
  const handleTitleChange = e => setTitle(e.currentTarget.value);
  const handleDescriptionChange = e => setDescription(e.currentTarget.value);
  const handleClose = () => {
    complete();
  };
  const handleDelete = () => {
    onEdit(id, {
      ...bar,
      title: '',
    });
  };

  const handleIsDone = () => setDone(!done);
  const handleColorChange = () => {
    const index = colorsList.indexOf(color) + 1;
    const finalIndex = index >= colorsList.length ? 0 : index;
    const newColor = colorsList[finalIndex];
    setColor(newColor as Color);
  };

  useEffect(() => {
    complete(true);
  }, [color, title, done, description]);

  // ---------------
  // HACK for Vimium extension
  // ---------------
  // const escFunction = e => {
  //   console.log(`Document: ${e.keyCode}`);
  //   if (e.keyCode === 27) {
  //     // Escape
  //     done();
  //   }
  // };
  // useEffect(() => {
  //   document.addEventListener('keydown', escFunction, false);
  //   return () => {
  //     document.removeEventListener('keydown', escFunction, false);
  //   };
  // }, []);
  // ---------------

  const handleKeyPress = e => {
    if (e.keyCode === 27) {
      // Escape
      complete();
    } else if (e.keyCode === 13) {
      // Enter
      complete();
    }
  };

  const handleFocus = e => e.currentTarget.select();
  const colorBg = colors[color].bg;
  const colorFg = colors[color].fg;

  return (
    <Popup x={x} y={y + 72} width={385} height={265} isOpen={isOpen} onClose={handleClose}>
      <div className="flex flex-col items-center justify-start w-full h-full">
        <div className={`w-full px-8 pt-5 pb-8 bg-${colorBg}`}>
          <div className="flex items-center justify-between w-full">
            <div>
              {!done && (
                <FaRegCheckCircle
                  size={40}
                  className={`p-2 -ml-2 text-${colorFg} text-opacity-50 cursor-pointer`}
                  onClick={handleIsDone}
                />
              )}
              {done && (
                <FaCheckCircle
                  size={40}
                  className={`p-2 -ml-2 text-${colorFg} text-opacity-100 cursor-pointer`}
                  onClick={handleIsDone}
                />
              )}
            </div>
            <div className="flex items-center">
              <div>
                <RiPaletteLine
                  className={`p-2 text-${colorFg} text-opacity-75 cursor-pointer`}
                  size={35}
                  onClick={handleColorChange}
                />
              </div>
              <div>
                <FaRegTrashAlt
                  className={`p-2 text-${colorFg} text-opacity-75 cursor-pointer`}
                  onClick={handleDelete}
                  size={33}
                />
              </div>
              <div>
                <MdClose
                  className={`p-2 -mr-4 text-${colorFg} text-opacity-75 cursor-pointer`}
                  onClick={handleClose}
                  size={35}
                />
              </div>
            </div>
          </div>
          <input
            className={`w-full pt-3 pb-2 text-${colorFg} placeholder-opacity-25 placeholder-${colorFg} text-lg font-semibold bg-transparent focus:outline-none`}
            autoFocus
            type="text"
            value={title}
            placeholder="New item"
            onChange={handleTitleChange}
            onKeyDown={handleKeyPress}
            onFocus={handleFocus}
          />
          <div className={`text-${colorFg} text-opacity-75 text-md`}>
            {shortDateFrom(bar.startDate)} &ndash; {shortDateFrom(bar.endDate)}
          </div>
        </div>
        <div className="w-full h-full p-6 bg-white">
          <input
            className="w-full bg-transparent text-md focus:outline-none"
            placeholder="Write a description"
            value={description}
            onChange={handleDescriptionChange}
            onKeyDown={handleKeyPress}
          />
        </div>
      </div>
    </Popup>
  );
};
